import * as React from 'react';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import ModifyTab from './ModifyTab';
import InfoTab from './InfoTab';
import MemoTab from './MemoTab';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { tokens } from "../../../theme";
import { useTheme } from "@mui/material";
import Axios from 'axios';
import Typography from '@mui/material/Typography';
import { Button } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { FileUploadOutlined } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import BackdropComp from '../../../components/Backdrop';
import { getAuthHeader } from '../../../utils/authToken';
import CallHistoryTab from '../../../voice/CallHistoryTab';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
    >
        {value === index && (
            <Box sx={{ p: 3 }}>
                {children}
            </Box>
        )}
    </div>
  );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export default function ModalTabs({ 
    student, 
    setStudentUpdated,
    setSuccessAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertOpen,
    setErrorAlertMessage, 
    socket = null,
    socketConnected = false,
    from_weekly = false,
    getStudentName,
}) {

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 500,
        height: 400,
        bgcolor: colors.primary[600],
        border: '2px solid grey',
        boxShadow: 24,
        p: 4,
        overflowY: 'auto',
    };

    const [value, setValue] = React.useState(0);

    const handleChange = (event, newValue) => {
        // The trailing "Upload Files" <Button> is the Tabs' last child and would otherwise be
        // "selected" when clicked; it sits after Info / Memos / Modify / Calls, i.e. at index 4.
        if (newValue === 4) {
            return;
        }
        setValue(newValue);
    };

    const [open, setOpen] = React.useState(false);
    const [removeTargetFile, setRemoveTargetFile] = React.useState(null);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    // for backdrop
    const [backdropOpen, setBackdropOpen] = React.useState(false);
    const handleBackdropClose = () => {
        setBackdropOpen(false);
    };

    // for file upload
    const [files, setFiles] = React.useState([]);

    const handleUpload = (event) => {
        const selectedFiles = Array.from(event.target.files);
        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    
        // Reset file input to allow selecting the same file again
        event.target.value = null;
    };

    // Remove file
    const handleFileRemoveFromList = (event, index) => {
        event.preventDefault();
        setFiles(files.filter((_, i) => i !== index));
    };

    // Handle Drag & Drop
    const handleDragOver = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const newFiles = Array.from(event.dataTransfer.files);
        setFiles((prev) => [...prev, ...newFiles]);
    };

    // for form
    const handleSubmit = async (event) => {
        event.preventDefault();        

        if (files.length === 0) {
            setErrorAlertMessage("Please upload a file to submit!");
            setErrorAlertOpen(true);
            return;
        }

        setBackdropOpen(true);
        
        const formData = new FormData();
        formData.append("student_id", student.uuid);
        for (let i = 0; i < files.length; i++) {

            if (files[i].size === 0) {
                setErrorAlertMessage("Empty file is not allowed!");
                setErrorAlertOpen(true);
                setBackdropOpen(false);
                return;
            }

            if (files[i].name !== undefined 
                && files[i].name !== null 
                && files[i].name !== ""
                && files[i].name.length > 150) {
                setErrorAlertMessage("File name should be less than 150 characters!");
                setErrorAlertOpen(true);
                setBackdropOpen(false);
                return;
            }

            formData.append('file', files[i], files[i].name);
        }

        await Axios.post(`${process.env.REACT_APP_URL}/api/v1/student_info_files`, formData, {
            headers: { Authorization: getAuthHeader() },
        })
        .then((res) => {
            if (!from_weekly) {
                // console.log("Server Response: ", res);
                setSuccessAlertMessage(res.data.message || "File uploaded successfully!");
                setSuccessAlertOpen(true);
                setStudentUpdated(true);           
            } else if (from_weekly && socketConnected) {
                // from weekly, so we need to emit to server (using socketio)
                socket.emit("put.StudentUpdate", {
                    student_uuid: student.uuid,
                    message: " uploaded " + getStudentName(student) + "'s info file",
                });
            }
            setFiles([]);
            setBackdropOpen(false);
            handleClose();
        })
        .catch((err) => {
            console.error("Upload Error: ", err);
            setErrorAlertMessage(err.response?.data?.error || "Failed to upload file");
            setErrorAlertOpen(true);
        });
    };

    const handleFileRemove = async () => {        
        setBackdropOpen(true);

        await Axios.delete(`${process.env.REACT_APP_URL}/api/v1/student_info_files/${removeTargetFile}`, {
            headers: { Authorization: getAuthHeader() },
        })
        .then((res) => {
            if (!from_weekly) {
                setSuccessAlertMessage(res.data.message || "File is deleted successfully!");
                setSuccessAlertOpen(true);
                setStudentUpdated(true);           
            } else if (from_weekly && socketConnected) {
                // from weekly, so we need to emit to server (using socketio)
                socket.emit("put.StudentUpdate", {
                    student_uuid: student.uuid,
                    message: " deleted " + getStudentName(student) + "'s info file",
                });
            }        
            setBackdropOpen(false);
        })
        .catch((err) => {
            console.error("Delete Error: ", err);
            setBackdropOpen(false);
            setErrorAlertMessage(err.response?.data?.error || "Failed to delete file");
            setErrorAlertOpen(true);
        });
    };

    return (
        <Box sx={{ width: '100%', height: '88%', overflowY: 'auto', overflowX: 'hidden' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={value} onChange={handleChange} aria-label="basic tabs" textColor="primary" indicatorColor="primary" >
                    <Tab label="Info" {...a11yProps(0)} />
                    <Tab label="Memos" {...a11yProps(1)} />
                    <Tab label="Modify" {...a11yProps(2)} />
                    <Tab label="Calls" {...a11yProps(3)} />
                    <Button onClick={handleOpen} sx={{ maxHeight: "48px", height: "48px" }}>
                        <FileUploadIcon fontSize='small' />
                        <span style={{ marginLeft: "5px" }}>
                            Upload Files
                        </span>
                    </Button>
                </Tabs>
            </Box>
            {/* Info tab */}
            <CustomTabPanel value={value} index={0}>
                <InfoTab 
                    student={student} 
                    handleFileRemove={handleFileRemove}
                    setRemoveTargetFile={setRemoveTargetFile}
                    removeTargetFile={removeTargetFile}
                />
            </CustomTabPanel>

            {/* Memos tab */}
            <CustomTabPanel value={value} index={1}>
                <MemoTab
                    memos={student.memos}
                    student_uuid={student.uuid}
                    setStudentUpdated={setStudentUpdated}
                    setSuccessAlertOpen={setSuccessAlertOpen}
                    setSuccessAlertMessage={setSuccessAlertMessage}
                    setErrorAlertOpen={setErrorAlertOpen}
                    setErrorAlertMessage={setErrorAlertMessage}
                    socket={socket}
                    socketConnected={socketConnected}
                    from_weekly={from_weekly}
                    getStudentName={getStudentName}
                />
            </CustomTabPanel>

            {/* Modify tab */}
            <CustomTabPanel value={value} index={2}>
                <ModifyTab 
                    student={student} 
                    setStudentUpdated={setStudentUpdated}
                    setSuccessAlertOpen={setSuccessAlertOpen}
                    setSuccessAlertMessage={setSuccessAlertMessage}
                    setErrorAlertOpen={setErrorAlertOpen}
                    setErrorAlertMessage={setErrorAlertMessage}
                    socket={socket}
                    socketConnected={socketConnected}
                    from_weekly={from_weekly}
                    getStudentName={getStudentName}
                />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={3}>
                <CallHistoryTab student={student} />
            </CustomTabPanel>

            {/* Upload file Modal */}
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                closeAfterTransition
                slotProps={{
                  backdrop: {
                    timeout: 500,
                  },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        {/* File Upload Section */}
                        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                            <Box
                                onClick={() => document.getElementById("fileUploadInput").click()}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                gap={2}
                                sx={{
                                    border: "2px dashed #aaa",
                                    padding: 3,
                                    borderRadius: 2,
                                    textAlign: "center",
                                    cursor: "pointer",
                                    backgroundColor: "#f5f5f5",
                                    "&:hover": { backgroundColor: "#e0e0e0" },
                                }}
                            >
                                <FileUploadOutlined fontSize="large" sx={{ color: colors.primary.main }} />
                                <Typography variant="body2" color="textSecondary">
                                    Click to upload PDF files
                                </Typography>

                                {/* Hidden File Input */}
                                <input
                                    id="fileUploadInput"
                                    multiple={true}
                                    type="file"
                                    hidden
                                    onChange={handleUpload}
                                />
                            </Box>

                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={2}
                                sx={{ flex: 1, overflowY: "auto" }}                                                                
                            >
                                {/* File List */}
                                {files.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        {files.map((file, index) => (
                                            <Box
                                                key={index}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                sx={{
                                                    padding: 1,
                                                    borderRadius: 2,
                                                    backgroundColor: "#fff",
                                                    mb: 1,
                                                    boxShadow: 1,
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {file.name}
                                                </Typography>
                                                <IconButton onClick={(e) => handleFileRemoveFromList(e, index)} size="small">
                                                    <ClearIcon sx={{ color: "red" }} />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{
                                    mt: 2,
                                    width: "100%",
                                    bgcolor: colors.primary.main,
                                    "&:hover": { bgcolor: colors.primary.light },
                                }}
                            >
                                Submit
                            </Button>
                        </form>
                        <BackdropComp backdropOpen={backdropOpen} handleBackdropClose={handleBackdropClose} />
                    </Box>
                </Fade>
            </Modal>
        </Box>        
    );
}